import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { colors } from '@/styles/colors'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow transition-all',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // The fill is a hardcoded indigo (see getVariantStyles), so the label is
        // hardcoded light to match. `text-primary-foreground` tracked the theme
        // while the fill did not, which put a dark glyph on dark indigo in dark
        // mode once --primary-foreground was corrected (UI-18).
        icon: 'text-white shadow [&_svg]:size-6 rounded-lg',
        tabInactive: 'hover:text-accent-foreground hover:bg-gray-200 dark:hover:bg-dark-border transition-colors',
        tabActive: 'bg-background shadow-sm hover:text-accent-foreground hover:bg-gray-50 dark:hover:bg-dark-muted transition-colors'
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        xl: 'h-14 rounded-md px-10 text-lg [&_svg]:size-6',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const ButtonBase = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, style, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    // Add inline styles for color variants
    const getVariantStyles = () => {
      if (variant === 'icon') {
        return { backgroundColor: colors.indigo[800], ...style }
      }
      if (variant === 'default') {
        return {
          ...style,
          ':hover': { backgroundColor: colors.indigo[800] }
        }
      }
      return style
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        style={getVariantStyles()}
        onMouseEnter={(e) => {
          if (variant === 'default') {
            e.currentTarget.style.backgroundColor = colors.indigo[800]
            // The hover fill is a fixed dark indigo, so the label has to be light
            // for the duration — `text-primary-foreground` is dark in dark mode
            // now that --primary is inverted (UI-18), and would vanish here.
            e.currentTarget.style.color = '#ffffff'
          } else if (variant === 'icon') {
            e.currentTarget.style.backgroundColor = colors.indigo[900]
          }
        }}
        onMouseLeave={(e) => {
          if (variant === 'icon') {
            e.currentTarget.style.backgroundColor = colors.indigo[800]
          } else if (variant === 'default') {
            e.currentTarget.style.backgroundColor = ''
            e.currentTarget.style.color = ''
          }
        }}
        {...props}
      />
    )
  }
)
ButtonBase.displayName = 'Button'

// --- uiv2: conditional Intui swap (build-time resolved by Vite) ---
import { ButtonV2Adapter } from './button-v2-adapter'

const _uiv2 = import.meta.env.VITE_UIV2 === 'true'
const Button = _uiv2 ? (ButtonV2Adapter as typeof ButtonBase) : ButtonBase

export { Button, ButtonBase, buttonVariants }
